import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  PhoneOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Empty,
  Progress,
  Skeleton,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {FaCalendarCheck, FaUserPlus} from 'react-icons/fa';
import {MdOutlineDashboard} from 'react-icons/md';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import {
  RQAppointmentLookupAPI,
  RqUserLookupAllRole,
} from '../../../ReactQuery/apis';
import {
  RQFollowUpLookupAPI,
  RQFollowUpTodayAPI,
} from '../../../ReactQuery/apis/rqFollowUpAPI';
import {selectAuth} from '../../../Redux/selectors';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const toDateStr = d => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
};

const today = new Date();
const todayStr = toDateStr(today);

const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowStr = toDateStr(tomorrow);

const weekEnd = new Date(today);
weekEnd.setDate(weekEnd.getDate() + 7);

const formatFriendlyDate = dateStr => {
  if (!dateStr) {
    return '—';
  }

  if (dateStr === todayStr) {
    return 'Today';
  }

  if (dateStr === tomorrowStr) {
    return 'Tomorrow';
  }

  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
};

const STATUS_COLORS = {
  PENDING: 'gold',
  COMPLETED: 'green',
  BOOKED: 'blue',
  CANCELLED: 'red',
  ACTIVE: 'blue',
  INACTIVE: 'default',
};

const AVATAR_PALETTE = [
  '#b69c66',
  '#7a9e9f',
  '#c97b63',
  '#6b8e9f',
  '#9e7ab6',
  '#7ab68e',
];

const getInitials = name => {
  if (!name) {
    return '?';
  }

  const parts = name.trim().split(/\s+/);

  return (
    (parts[0]?.[0] || '') +
    (parts[1]?.[0] || '')
  ).toUpperCase();
};

const getAvatarColor = name => {
  if (!name) {
    return AVATAR_PALETTE[0];
  }

  const hash = name
    .split('')
    .reduce(
      (acc, ch) => acc + ch.charCodeAt(0),
      0,
    );

  return AVATAR_PALETTE[
    hash % AVATAR_PALETTE.length
  ];
};

/* -------------------------------------------------------------------------- */
/* Section Card                                                               */
/* -------------------------------------------------------------------------- */

const SectionCard = ({
  icon,
  title,
  count,
  countColor = 'gold',
  children,
  extra,
}) => (
  <Card
    className={'rounded-xl border border-primary-100 shadow-sm h-full'}
    styles={{
      body: {
        padding: '16px 20px',
      },
    }}
  >
    <div className={'flex items-center justify-between mb-3'}>
      <Space size={8}>
        <span className={'text-primary-500 text-base'}>
          {icon}
        </span>

        <span className={'font-semibold text-gray-800 text-[15px]'}>
          {title}
        </span>

        {count !== undefined && (
          <Tag
            color={countColor}
            className={'ml-1'}
          >
            {count}
          </Tag>
        )}
      </Space>

      {extra}
    </div>

    <Divider className={'!my-2'} />

    {children}
  </Card>
);

SectionCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  count: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  countColor: PropTypes.string,
  children: PropTypes.node,
  extra: PropTypes.node,
};

/* -------------------------------------------------------------------------- */
/* Dashboard List                                                             */
/* -------------------------------------------------------------------------- */

const DashboardList = ({
  dataSource = [],
  renderItem,
  loading = false,
  split = true,
  emptyDescription = 'No data',
}) => {
  if (
    loading &&
    dataSource.length === 0
  ) {
    return (
      <div className={'space-y-3 py-2'}>
        {[1, 2, 3].map(index => (
          <div
            key={index}
            className={'flex items-center gap-3 py-3'}
          >
            <Skeleton.Avatar
              active
              size={38}
            />

            <div className={'flex-1'}>
              <Skeleton.Input
                active
                size={'small'}
                block
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (dataSource.length === 0) {
    return (
      <Empty
        description={emptyDescription}
        image={
          Empty.PRESENTED_IMAGE_SIMPLE
        }
      />
    );
  }

  return (
    <div className={'w-full'}>
      {dataSource.map((item, index) => (
        <React.Fragment
          key={
            item?.id ||
            item?._id ||
            `${index}-${JSON.stringify(item)}`
          }
        >
          {renderItem(item, index)}

          {split &&
            index !==
              dataSource.length - 1 && (
            <Divider
              className={'!my-0'}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

DashboardList.propTypes = {
  dataSource: PropTypes.array,
  renderItem: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  split: PropTypes.bool,
  emptyDescription: PropTypes.string,
};

/* -------------------------------------------------------------------------- */
/* Main Dashboard                                                             */
/* -------------------------------------------------------------------------- */

const MainDashboard = () => {
  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  /* ------------------------------------------------------------------------ */
  /* Role                                                                     */
  /* ------------------------------------------------------------------------ */

  const role = auth?.user?.role;

  const basePath = role
    ? `/${role.toLowerCase()}`
    : '';

  const currentUserId = auth?.user?.id;

  const isDoctorScoped =
    role === 'DOCTOR' ||
    role === 'THERAPIST';

  /* ------------------------------------------------------------------------ */
  /* API Queries                                                              */
  /* ------------------------------------------------------------------------ */

  const {
    data: patients = [],
    isFetching: patientsLoading,
    refetch: refetchPatients,
  } = RqUserLookupAllRole(
    'PATIENT',
    token,
  );

  const {
    data: appointments = [],
    isFetching: appointmentsLoading,
    refetch: refetchAppointments,
  } = RQAppointmentLookupAPI(token);

  const {
    data: allFollowUps = [],
    isFetching: followUpsLoading,
    refetch: refetchFollowUps,
  } = RQFollowUpLookupAPI(token);

  const {
    data: todayFollowUps = [],
    isFetching: todayFollowUpsLoading,
    refetch: refetchTodayFollowUps,
  } = RQFollowUpTodayAPI(token);

  /* ------------------------------------------------------------------------ */
  /* Loading                                                                  */
  /* ------------------------------------------------------------------------ */

  const loading =
    patientsLoading ||
    appointmentsLoading ||
    followUpsLoading ||
    todayFollowUpsLoading;

  /* ------------------------------------------------------------------------ */
  /* Refresh                                                                  */
  /* ------------------------------------------------------------------------ */

  const handleRefreshAll = () => {
    refetchPatients();
    refetchAppointments();
    refetchFollowUps();
    refetchTodayFollowUps();
  };

  /* ------------------------------------------------------------------------ */
  /* Scoped Data                                                              */
  /* ------------------------------------------------------------------------ */

  const scopedFollowUps = useMemo(
    () =>
      isDoctorScoped
        ? allFollowUps.filter(
          followUp =>
            followUp.doctorId ===
            currentUserId,
        )
        : allFollowUps,
    [
      allFollowUps,
      isDoctorScoped,
      currentUserId,
    ],
  );

  const scopedTodayFollowUps = useMemo(
    () =>
      isDoctorScoped
        ? todayFollowUps.filter(
          followUp =>
            followUp.doctorId ===
            currentUserId,
        )
        : todayFollowUps,
    [
      todayFollowUps,
      isDoctorScoped,
      currentUserId,
    ],
  );

  const scopedAppointments = useMemo(
    () =>
      isDoctorScoped
        ? appointments.filter(
          appointment =>
            appointment.doctorId ===
            currentUserId,
        )
        : appointments,
    [
      appointments,
      isDoctorScoped,
      currentUserId,
    ],
  );

  /* ------------------------------------------------------------------------ */
  /* Follow-ups                                                               */
  /* ------------------------------------------------------------------------ */

  const tomorrowFollowUps = useMemo(
    () =>
      scopedFollowUps.filter(
        followUp =>
          followUp.nextVisit ===
          tomorrowStr,
      ),
    [scopedFollowUps],
  );

  const thisWeekFollowUps = useMemo(
    () =>
      scopedFollowUps
        .filter(followUp => {
          if (!followUp.nextVisit) {
            return false;
          }

          const date = new Date(
            followUp.nextVisit,
          );

          return (
            date >= today &&
            date <= weekEnd &&
            followUp.status !==
              'COMPLETED'
          );
        })
        .sort((a, b) =>
          a.nextVisit.localeCompare(
            b.nextVisit,
          ),
        ),
    [scopedFollowUps],
  );

  const completedFollowUpsCount =
    useMemo(
      () =>
        scopedFollowUps.filter(
          followUp =>
            followUp.status ===
            'COMPLETED',
        ).length,
      [scopedFollowUps],
    );

  const pendingFollowUpsCount =
    scopedFollowUps.length -
    completedFollowUpsCount;

  const completionRate =
    scopedFollowUps.length
      ? Math.round(
        (completedFollowUpsCount /
          scopedFollowUps.length) *
        100,
      )
      : 0;

  /* ------------------------------------------------------------------------ */
  /* Appointments                                                             */
  /* ------------------------------------------------------------------------ */

  const todaysAppointments = useMemo(
    () =>
      scopedAppointments.filter(
        appointment =>
          appointment.appointmentDate ===
          todayStr,
      ),
    [scopedAppointments],
  );

  const upcomingAppointments = useMemo(
    () =>
      scopedAppointments
        .filter(
          appointment =>
            appointment.appointmentDate >=
              todayStr &&
            appointment.status !==
              'CANCELLED',
        )
        .sort((a, b) =>
          a.appointmentDate.localeCompare(
            b.appointmentDate,
          ),
        )
        .slice(0, 6),
    [scopedAppointments],
  );

  /* ------------------------------------------------------------------------ */
  /* Recent Patients                                                          */
  /* ------------------------------------------------------------------------ */

  const recentPatients = useMemo(
    () =>
      [...patients]
        .sort(
          (a, b) =>
            new Date(
              b.createdOn ?? 0,
            ) -
            new Date(
              a.createdOn ?? 0,
            ),
        )
        .slice(0, 5),
    [patients],
  );

  /* ------------------------------------------------------------------------ */
  /* Weekly Pagination                                                        */
  /* ------------------------------------------------------------------------ */

  const [weeklyPage, setWeeklyPage] =
    useState(1);

  const weeklyPageSize = 5;

  const weeklyTotalPages = Math.max(
    1,
    Math.ceil(
      thisWeekFollowUps.length /
        weeklyPageSize,
    ),
  );

  const paginatedWeeklyFollowUps =
    useMemo(() => {
      const start =
        (weeklyPage - 1) *
        weeklyPageSize;

      return thisWeekFollowUps.slice(
        start,
        start + weeklyPageSize,
      );
    }, [
      thisWeekFollowUps,
      weeklyPage,
    ]);

  useEffect(() => {
    if (
      weeklyPage >
      weeklyTotalPages
    ) {
      setWeeklyPage(
        weeklyTotalPages,
      );
    }
  }, [
    weeklyPage,
    weeklyTotalPages,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Follow-up Item                                                           */
  /* ------------------------------------------------------------------------ */

  const renderFollowUpItem =
    followUp => (
      <div
        className={
          'px-0 py-3 w-full'
        }
      >
        <div
          className={
            'flex items-start justify-between w-full gap-3'
          }
        >
          <div
            className={
              'flex items-start gap-3 min-w-0'
            }
          >
            <Avatar
              style={{
                backgroundColor:
                  getAvatarColor(
                    followUp.patientName,
                  ),
              }}
              size={38}
            >
              {getInitials(
                followUp.patientName,
              )}
            </Avatar>

            <div
              className={
                'min-w-0'
              }
            >
              <div
                className={
                  'font-medium text-gray-800 truncate'
                }
              >
                {followUp.patientName ||
                  'Unknown patient'}
              </div>

              {followUp.remarks && (
                <div
                  className={
                    'text-xs text-gray-500 italic mt-0.5 truncate max-w-[220px]'
                  }
                >
                  &quot;
                  {followUp.remarks}
                  &quot;
                </div>
              )}
            </div>
          </div>

          <div
            className={
              'text-xs text-gray-400'
            }
          >
            Previous visit{' '}
            {followUp.previousVisit ||
              '—'}
          </div>

          <div
            className={
              'text-right shrink-0'
            }
          >
            <div
              className={
                'text-xs text-gray-500 mb-1'
              }
            >
              {formatFriendlyDate(
                followUp.nextVisit,
              )}
            </div>

            <Tag
              color={
                STATUS_COLORS[
                  followUp.status
                ] || 'default'
              }
              className={'m-0'}
            >
              {followUp.status}
            </Tag>
          </div>
        </div>
      </div>
    );

  /* ------------------------------------------------------------------------ */
  /* Appointment Item                                                         */
  /* ------------------------------------------------------------------------ */

  const renderAppointmentItem =
    appointment => (
      <div
        className={
          'px-0 py-3 w-full'
        }
      >
        <div
          className={
            'flex items-start justify-between w-full gap-3'
          }
        >
          <div
            className={
              'flex items-start gap-3 min-w-0'
            }
          >
            <Avatar
              style={{
                backgroundColor:
                  getAvatarColor(
                    appointment.patientName,
                  ),
              }}
              size={38}
            >
              {getInitials(
                appointment.patientName,
              )}
            </Avatar>

            <div
              className={
                'min-w-0'
              }
            >
              <div
                className={
                  'font-medium text-gray-800 truncate'
                }
              >
                {
                  appointment.patientName
                }
              </div>

              <div
                className={
                  'text-xs text-gray-400'
                }
              >
                Dr.{' '}
                {
                  appointment.doctorName
                }{' '}
                {appointment.specialization
                  ? `· ${appointment.specialization}`
                  : ''}
              </div>
            </div>
          </div>

          <div
            className={
              'text-right shrink-0'
            }
          >
            <div
              className={
                'text-xs text-gray-600 font-medium'
              }
            >
              {formatFriendlyDate(
                appointment.appointmentDate,
              )}
            </div>

            <Tag
              color={
                STATUS_COLORS[
                  appointment.status
                ] || 'default'
              }
              className={
                'm-0 mt-1'
              }
            >
              {appointment.status}
            </Tag>
          </div>
        </div>
      </div>
    );

  /* ------------------------------------------------------------------------ */
  /* Patient Item                                                             */
  /* ------------------------------------------------------------------------ */

  const renderPatientItem =
    patient => (
      <div
        className={
          'px-0 py-3 w-full'
        }
      >
        <div
          className={
            'flex items-center justify-between w-full gap-3'
          }
        >
          <div
            className={
              'flex items-center gap-3 min-w-0'
            }
          >
            <Avatar
              style={{
                backgroundColor:
                  getAvatarColor(
                    patient.fullName,
                  ),
              }}
              size={38}
            >
              {getInitials(
                patient.fullName,
              )}
            </Avatar>

            <div
              className={
                'min-w-0'
              }
            >
              <div
                className={
                  'font-medium text-gray-800 truncate'
                }
              >
                {patient.fullName}
              </div>

              <Space
                size={10}
                className={
                  'text-xs text-gray-400'
                }
              >
                <span>
                  <PhoneOutlined
                    className={
                      'mr-1'
                    }
                  />
                  {
                    patient.mobileNumber
                  }
                </span>
              </Space>
            </div>
          </div>

          <div
            className={
              'text-right shrink-0'
            }
          >
            <div
              className={
                'text-xs text-gray-400 mb-1'
              }
            >
              {patient.createdOn
                ? new Date(
                  patient.createdOn,
                ).toLocaleDateString(
                  'en-IN',
                  {
                    day: 'numeric',
                    month: 'short',
                  },
                )
                : '—'}
            </div>

            <Tag
              color={
                STATUS_COLORS[
                  patient.status
                ] || 'default'
              }
              className={'m-0'}
            >
              {patient.status}
            </Tag>
          </div>
        </div>
      </div>
    );

  /* ------------------------------------------------------------------------ */
  /* Stat Cards                                                               */
  /* ------------------------------------------------------------------------ */

  const statCards = [
    {
      label: 'Total Patients',
      count: patients.length,
      icon: (
        <FaUserPlus
          className={
            'md:text-4xl text-3xl'
          }
        />
      ),
      to: `${basePath}/patients`,
      loading: patientsLoading,
    },
    {
      label: isDoctorScoped
        ? 'My Appointments'
        : 'Total Appointments',
      count: scopedAppointments.length,
      icon: (
        <FaCalendarCheck
          className={
            'md:text-4xl text-3xl'
          }
        />
      ),
      to: `${basePath}/appointments`,
      loading: appointmentsLoading,
    },
    {
      label: 'Today\'s Follow-ups',
      count:
        scopedTodayFollowUps.length,
      icon: (
        <ClockCircleOutlined
          className={
            'md:text-4xl text-3xl'
          }
        />
      ),
      to: `${basePath}/dashboard`,
      loading:
        todayFollowUpsLoading,
    },
    {
      label: 'Pending Follow-ups',
      count: pendingFollowUpsCount,
      icon: (
        <FieldTimeOutlined
          className={
            'md:text-4xl text-3xl'
          }
        />
      ),
      to: `${basePath}/dashboard`,
      loading: followUpsLoading,
    },
  ];

  /* ------------------------------------------------------------------------ */
  /* JSX                                                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className={
        'min-h-screen md:p-6 p-2'
      }
    >
      {/* Header */}
      <div
        className={
          'flex items-center justify-between mb-6 flex-wrap gap-3'
        }
      >
        <div
          className={
            'flex items-center gap-3'
          }
        >
          <div
            className={
              'w-12 h-10 rounded-xl flex items-center justify-center text-white'
            }
            style={{
              background:
                'linear-gradient(135deg, #b69c66, #b69c66)',
            }}
          >
            <MdOutlineDashboard
              style={{
                fontSize: 22,
              }}
            />
          </div>

          <h1
            className={
              'title !mb-0'
            }
          >
            Dashboard
          </h1>
        </div>

        <Button
          icon={
            <ReloadOutlined />
          }
          onClick={
            handleRefreshAll
          }
          loading={loading}
          className={
            'btn-secondary'
          }
        >
          Refresh
        </Button>
      </div>

      {/* Stat Cards */}
      <div
        className={
          'flex justify-center items-center text-center mb-6'
        }
      >
        <div
          className={
            'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 w-full'
          }
        >
          {statCards.map(
            ({
              label,
              count,
              icon,
              to,
              loading:
                cardLoading,
            }) => (
              <Link
                key={label}
                to={to}
              >
                <div
                  className={
                    'relative overflow-hidden rounded-lg bg-gradient-to-br from-primary-900 to-primary-600 p-6 h-40 shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl cursor-pointer'
                  }
                >
                  {/* Background Circles */}
                  <div
                    className={
                      'absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10'
                    }
                  />

                  <div
                    className={
                      'absolute -right-10 -bottom-10 h-32 w-32 rounded-full bg-white/5'
                    }
                  />

                  <div
                    className={
                      'relative z-10 flex flex-col justify-between h-full'
                    }
                  >
                    <div
                      className={
                        'flex justify-between items-start'
                      }
                    >
                      <div
                        className={
                          'bg-white/20 backdrop-blur-md p-4 mt-4 rounded-full text-gold text-2xl'
                        }
                      >
                        {icon}
                      </div>

                      <div
                        className={
                          'w-full flex flex-col justify-center text-center items-center'
                        }
                      >
                        <p
                          className={
                            'text-white/90 text-base font-medium uppercase tracking-wider'
                          }
                        >
                          {label}
                        </p>

                        <h2
                          className={
                            'text-5xl font-bold text-gold mt-3'
                          }
                        >
                          {cardLoading ? (
                            <Skeleton.Input
                              active
                              size={'small'}
                              style={{
                                width: 60,
                              }}
                            />
                          ) : (
                            count
                          )}
                        </h2>
                      </div>
                    </div>

                    <div
                      className={
                        'text-white/70 flex ml-4 w-full justify-center text-center items-center text-sm font-medium'
                      }
                    >
                      View Details →
                    </div>
                  </div>
                </div>
              </Link>
            ),
          )}
        </div>
      </div>

      {/* Today / Tomorrow Follow-ups */}
      <div
        className={
          'grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4'
        }
      >
        <SectionCard
          icon={
            <CheckCircleOutlined />
          }
          title={(
            <div
              className={
                'text-base font-bold font-serif text-primary-700'
              }
            >
              Todays Follow-ups
            </div>
          )}
          count={
            scopedTodayFollowUps.length
          }
          countColor={'gold'}
        >
          {scopedTodayFollowUps.length ===
          0 ? (
              <Empty
                description={
                  'Nothing scheduled for today'
                }
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
              />
            ) : (
              <DashboardList
                dataSource={
                  scopedTodayFollowUps
                }
                renderItem={
                  renderFollowUpItem
                }
                split
                loading={
                  todayFollowUpsLoading
                }
              />
            )}
        </SectionCard>

        <SectionCard
          icon={
            <CalendarOutlined />
          }
          title={(
            <div
              className={
                'text-base font-bold font-serif text-primary-700'
              }
            >
              Tomorrows Follow-ups
            </div>
          )}
          count={
            tomorrowFollowUps.length
          }
          countColor={'blue'}
        >
          {tomorrowFollowUps.length ===
          0 ? (
              <Empty
                description={
                  'Nothing scheduled for tomorrow'
                }
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
              />
            ) : (
              <DashboardList
                dataSource={
                  tomorrowFollowUps
                }
                renderItem={
                  renderFollowUpItem
                }
                split
                loading={
                  followUpsLoading
                }
              />
            )}
        </SectionCard>
      </div>

      {/* This week's follow-ups + completion progress */}
      <div
        className={
          'grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4'
        }
      >
        <div
          className={
            'xl:col-span-2'
          }
        >
          <SectionCard
            icon={
              <FieldTimeOutlined />
            }
            title={(
              <div
                className={
                  'text-base font-bold font-serif text-primary-700'
                }
              >
                This Weeks Follow-ups
              </div>
            )}
            count={
              thisWeekFollowUps.length
            }
            countColor={'purple'}
          >
            {thisWeekFollowUps.length ===
            0 ? (
                <Empty
                  description={
                    'No follow-ups scheduled this week'
                  }
                  image={
                    Empty.PRESENTED_IMAGE_SIMPLE
                  }
                />
              ) : (
                <>
                  <DashboardList
                    dataSource={
                      paginatedWeeklyFollowUps
                    }
                    renderItem={
                      renderFollowUpItem
                    }
                    split
                    loading={
                      followUpsLoading
                    }
                  />

                  {weeklyTotalPages >
                    1 && (
                    <div
                      className={
                        'flex justify-center items-center gap-2 mt-4'
                      }
                    >
                      <Button
                        size={'small'}
                        disabled={
                          weeklyPage ===
                          1
                        }
                        onClick={() =>
                          setWeeklyPage(
                            previousPage =>
                              Math.max(
                                1,
                                previousPage -
                                  1,
                              ),
                          )
                        }
                      >
                        Previous
                      </Button>

                      <span
                        className={
                          'text-sm text-gray-500 px-2'
                        }
                      >
                        Page{' '}
                        {
                          weeklyPage
                        }{' '}
                        of{' '}
                        {
                          weeklyTotalPages
                        }
                      </span>

                      <Button
                        size={'small'}
                        disabled={
                          weeklyPage ===
                          weeklyTotalPages
                        }
                        onClick={() =>
                          setWeeklyPage(
                            previousPage =>
                              Math.min(
                                weeklyTotalPages,
                                previousPage +
                                  1,
                              ),
                          )
                        }
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
          </SectionCard>
        </div>

        {/* Follow-up Completion */}
        <Card
          className={
            'rounded-xl border border-primary-100 shadow-sm h-full'
          }
          styles={{
            body: {
              padding: '20px',
            },
          }}
        >
          <div
            className={
              'font-semibold text-primary-700 text-base font-serif mb-4'
            }
          >
            Follow-up Completion
          </div>

          <div
            className={
              'flex flex-col items-center justify-center py-2'
            }
          >
            <Progress
              type={'circle'}
              percent={
                completionRate
              }
              size={120}
              strokeColor={
                '#b69c66'
              }
            />

            <div
              className={
                'mt-4 w-full'
              }
            >
              <div
                className={
                  'flex justify-between text-sm mb-1'
                }
              >
                <span
                  className={
                    'text-gray-500'
                  }
                >
                  Completed
                </span>

                <span
                  className={
                    'font-medium text-gray-800'
                  }
                >
                  {
                    completedFollowUpsCount
                  }
                </span>
              </div>

              <div
                className={
                  'flex justify-between text-sm'
                }
              >
                <span
                  className={
                    'text-gray-500'
                  }
                >
                  Pending
                </span>

                <span
                  className={
                    'font-medium text-gray-800'
                  }
                >
                  {
                    pendingFollowUpsCount
                  }
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Upcoming Appointments + Recent Patients */}
      <div
        className={
          'grid grid-cols-1 xl:grid-cols-2 gap-4'
        }
      >
        {/* Upcoming Appointments */}
        <SectionCard
          icon={
            <CalendarOutlined />
          }
          title={(
            <div
              className={
                'text-base font-bold font-serif text-primary-700'
              }
            >
              Upcoming Appointments
            </div>
          )}
          extra={(
            <Tooltip
              title={`${todaysAppointments.length} today`}
            >
              <Badge
                count={
                  todaysAppointments.length
                }
                color={
                  '#b69c66'
                }
              />
            </Tooltip>
          )}
        >
          {upcomingAppointments.length ===
          0 ? (
              <Empty
                description={
                  'No upcoming appointments'
                }
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
              />
            ) : (
              <DashboardList
                dataSource={
                  upcomingAppointments
                }
                renderItem={
                  renderAppointmentItem
                }
                split
                loading={
                  appointmentsLoading
                }
              />
            )}
        </SectionCard>

        {/* Recently Registered Patients */}
        <SectionCard
          icon={
            <TeamOutlined />
          }
          title={(
            <div
              className={
                'text-base font-bold font-serif text-primary-700'
              }
            >
              Recently Registered Patients
            </div>
          )}
        >
          {recentPatients.length ===
          0 ? (
              <Empty
                description={
                  'No patients registered yet'
                }
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
              />
            ) : (
              <DashboardList
                dataSource={
                  recentPatients
                }
                renderItem={
                  renderPatientItem
                }
                split
                loading={
                  patientsLoading
                }
              />
            )}
        </SectionCard>
      </div>
    </div>
  );
};

export {
  MainDashboard,
};

export default MainDashboard;
